import { useState, useEffect } from 'react';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: "サービス内容", anchor: "#services" },
    { label: "料金", anchor: "#pricing" },
    { label: "よくある質問", anchor: "#faq" },
    { label: "お問い合わせ", anchor: "#contact" }
  ];

  const scrollToSection = (anchor: string) => {
    const element = document.querySelector(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/${anchor}`;
    }
    setIsMenuOpen(false);
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-100' 
        : 'bg-white/85 backdrop-blur-md border-b border-gray-100 shadow-sm'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 max-w-7xl mx-auto">
          {/* Logo */}
          <a href="/#hero" className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
            <img 
              src="https://static.readdy.ai/image/ea3bba43a5d95f491f17f4f7543903eb/b47aa0060d508a8fee7fb7f43f1bd082.png" 
              alt="ソトマゴ" 
              className="h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden md:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
            <div className="flex items-center space-x-10">
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(item.anchor)}
                  className="relative text-brand-dark hover:text-brand-yellow transition-all duration-300 font-medium cursor-pointer whitespace-nowrap text-[15px] group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-yellow transition-all duration-300 group-hover:w-full"></span>
                </button>
              ))}
            </div>
          </nav>

          {/* Desktop CTAs - Right aligned and fixed to right edge */}
          <div className="hidden md:flex items-center space-x-3 flex-shrink-0 ml-auto">
            <a
              href="tel:+817090045641"
              className="flex items-center px-4 py-2.5 text-brand-yellow hover:text-brand-yellow-dark transition-all duration-300 font-bold cursor-pointer whitespace-nowrap text-xl hover:scale-105"
            >
              <i className="ri-phone-line mr-2 text-2xl"></i>
              070-9004-5641
            </a>
            <a
              href="https://www.instagram.com/sotomago_tottori/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer rounded-full hover:opacity-80"
            >
              <i className="ri-instagram-line text-2xl text-[#333333]"></i>
            </a>
            <a
              href="https://line.me/R/ti/p/@251xqjgz?_gl=1*8soeuu*_gcl_au*MTUyNTMzMzgwOC4xNzU5NzQyMTgz*_ga*NjgyMTMyNjIuMTc1OTc0MjE4NA..*_ga_0PV16Y9CZG*czE3NjQ3NTgwMzUkbzIkZzAkdDE3NjQ3NTgwNDEkajU0JGwwJGgw"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer rounded-full hover:opacity-80"
            >
              <i className="ri-line-line text-2xl text-[#333333]"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-brand-yellow-light rounded-lg transition-all duration-300"
          >
            <i className={`text-xl text-brand-dark transition-transform duration-300 ${isMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-6 border-t border-gray-100 animate-fade-in">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(item.anchor)}
                  className="text-left text-brand-dark hover:text-brand-yellow transition-all duration-300 font-medium cursor-pointer whitespace-nowrap py-2 px-4 rounded-lg hover:bg-brand-yellow-light"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <a
                  href="tel:+817090045641"
                  className="flex items-center text-brand-yellow hover:text-brand-yellow-dark transition-all duration-300 font-bold cursor-pointer whitespace-nowrap text-lg py-2 px-4"
                >
                  <i className="ri-phone-line mr-2 text-xl"></i>
                  070-9004-5641
                </a>
                <a
                  href="https://www.instagram.com/sotomago_tottori/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-brand-dark hover:text-brand-yellow transition-all duration-300 cursor-pointer whitespace-nowrap py-2 px-4 rounded-lg hover:bg-brand-yellow-light"
                >
                  <i className="ri-instagram-line text-2xl text-[#333333] mr-2"></i>
                  Instagram
                </a>
                <a
                  href="https://line.me/R/ti/p/@251xqjgz?_gl=1*8soeuu*_gcl_au*MTUyNTMzMzgwOC4xNzU5NzQyMTgz*_ga*NjgyMTMyNjIuMTc1OTc0MjE4NA..*_ga_0PV16Y9CZG*czE3NjQ3NTgwMzUkbzIkZzAkdDE3NjQ3NTgwNDEkajU0JGwwJGgw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-brand-yellow text-brand-dark px-6 py-3 rounded-xl font-bold hover:bg-brand-yellow-dark transition-all duration-300 whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg"
                >
                  <i className="ri-line-line mr-2"></i>
                  LINEで相談
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
